import React, { useCallback, useMemo } from 'react'
import { FanVotingProps } from '@sport1/types/web'
import { useFanVoting } from '@sport1/bets-service-client'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import { MatchClientModel } from '@sport1/types/sportData'
import CardHeadline from '@sport1/news-styleguide/CardHeadline'
import FanVotingOptions from '@/components/FanVoting/VotingOptions'
import { OddsDisclaimerContextProvider } from '@/utils/oddsDisclaimer/OddsDisclaimerProvider'
import { useTracking } from '@/utils/tracking/TrackingProvider'
import { getSportsMatchData } from '@/utils/tracking/SportEventTrackingData'

type Props = Omit<FanVotingProps, 'type'> & {
    match?: MatchClientModel
}

const FanVoting = ({ params, headline, match: initialMatch }: Props) => {
    const { competitionId, matchId, matchPeriod, isMatchLive } = params
    const { trackInteraction } = useTracking()
    const { fanVotingUIProperties, onVote } = useFanVoting(params)

    const match = useMemo(
        () =>
            initialMatch || {
                id: matchId,
                period: matchPeriod,
                isLive: isMatchLive,
                competition: {
                    id: competitionId,
                },
            },
        [initialMatch, matchId, matchPeriod, isMatchLive, competitionId]
    )

    const vote = useCallback(
        (selectedIndex: number) => {
            onVote(selectedIndex)
            trackInteraction({
                interaction_category: 'fanvoting',
                interaction_action: 'click',
                interaction_label: getSportsMatchData(match),
            })
        },
        [match, onVote, trackInteraction]
    )

    return fanVotingUIProperties?.renderFanVotingComponent &&
        fanVotingUIProperties.fanVotingData?.votingOptions?.length ? (
        <NonFlexingContainer marginBottom="spacing-9" testID={`fanvoting-${match.id}`}>
            <NonFlexingContainer paddingBottom="spacing-7">
                <CardHeadline
                    text={headline}
                    cardHeadlineType={['cardHeadlineS', 'cardHeadlineM', 'cardHeadlineS']}
                />
            </NonFlexingContainer>
            <OddsDisclaimerContextProvider>
                <FanVotingOptions
                    title={fanVotingUIProperties.fanVotingData.title}
                    votingOptions={fanVotingUIProperties.fanVotingData.votingOptions}
                    hasVoted={fanVotingUIProperties.hasVoted}
                    available={fanVotingUIProperties.fanVotingData.votingAvailable}
                    onVote={vote}
                    match={match}
                />
            </OddsDisclaimerContextProvider>
        </NonFlexingContainer>
    ) : null
}

export default FanVoting
